import type {
  TCreateInstituteWireRequest,
  TCreateInstituteWireResponse,
  TGetInstituteByIdWire,
  TGetInstituteWire,
} from "./institute.wire.interfaces";
import type {
  TInstitute,
  TInstituteCreateStatus,
  TInstituteStatus,
  TInstituteType,
} from "./institute.interfaces";
import { INSTITUTE_STATUSES } from "./institute.interfaces";

const BOARD_TYPES_SATISFYING_DOMAIN = [
  "CBSE",
  "ICSE",
  "State Board",
  "IB",
  "IGCSE",
  "Other",
] as const satisfies readonly TInstituteType[];

const BOARD_TYPE_SET = new Set<string>(BOARD_TYPES_SATISFYING_DOMAIN);

const INSTITUTE_STATUS_SET = new Set<string>(INSTITUTE_STATUSES);

/** Map wire/API status strings to {@link TInstituteStatus}; tolerates legacy `inactive`. */
export const parseWireInstituteStatus = (
  raw: unknown,
  fallback: TInstituteStatus = "pending",
): TInstituteStatus => {
  if (typeof raw !== "string" || raw === "") return fallback;
  if (raw === "inactive") return "other";
  if (INSTITUTE_STATUS_SET.has(raw)) return raw as TInstituteStatus;
  return fallback;
};

export const NIL_UUID = "00000000-0000-0000-0000-000000000000";

/** API may return `email` as a plain string or `{ email, is_verified }`. */
export const normalizeWireContactEmail = (
  raw: unknown,
): { email?: string; isVerified?: boolean } => {
  if (raw == null || raw === "") return {};

  if (typeof raw === "string") {
    const trimmed = raw.trim();
    return trimmed ? { email: trimmed } : {};
  }

  if (
    typeof raw === "object" &&
    raw !== null &&
    "email" in (raw as object)
  ) {
    const o = raw as {
      email?: unknown;
      is_verified?: unknown;
      isVerified?: unknown;
    };
    const trimmed =
      typeof o.email === "string" ? o.email.trim() : undefined;
    const email = trimmed === "" ? undefined : trimmed;
    const isVerified =
      typeof o.is_verified === "boolean"
        ? o.is_verified
        : typeof o.isVerified === "boolean"
          ? o.isVerified
          : undefined;
    const out: { email?: string; isVerified?: boolean } = {};
    if (email) out.email = email;
    if (isVerified !== undefined) out.isVerified = isVerified;
    return out;
  }

  return {};
};

/**
 * When PATCH/GET returns institute JSON with nested `contactInformation.email`,
 * coerce to plain string + optional `emailVerified`.
 */
export const normalizeTInstituteContactEmail = (
  institute: TInstitute,
): TInstitute => {
  const ci = institute.contactInformation;
  const rawEmail = ci?.email as unknown;
  if (
    !ci ||
    rawEmail === undefined ||
    rawEmail === null ||
    typeof rawEmail !== "object" ||
    !("email" in rawEmail)
  ) {
    return institute;
  }
  const n = normalizeWireContactEmail(rawEmail);
  return {
    ...institute,
    contactInformation: {
      ...ci,
      email: n.email,
      emailVerified: n.isVerified ?? ci.emailVerified,
    },
  };
};

type TLegacyEmbedded = {
  type: TInstitute["type"];
  registrationNumber: string;
  registrationDate: string;
  legalEntity: string;
};

/**
 * Read `_adminLegacy` from decoded `values` (JSON string) or from a structured `values` object.
 */
export const parseLegacyEmbeddedFromWireValues = (
  values: unknown,
): TLegacyEmbedded | null => {
  if (
    typeof values === "object" &&
    values !== null &&
    "_adminLegacy" in values
  ) {
    const raw = (values as { _adminLegacy?: unknown })._adminLegacy;
    if (!raw || typeof raw !== "object") return null;
    return raw as TLegacyEmbedded;
  }

  if (typeof values !== "string" || values === "") return null;

  try {
    const parsed = JSON.parse(values) as {
      _adminLegacy?: TLegacyEmbedded;
    };
    return parsed?._adminLegacy ?? null;
  } catch {
    return null;
  }
};

/** Align by-id wire DTO with list item shape consumed by {@link mapGetInstituteWireToTInstitute}. */
export const normalizeGetInstituteByIdWire = (
  wire: TGetInstituteByIdWire,
): TGetInstituteWire => ({
  id: wire.id,
  name: wire.name,
  status: wire.status,
  description: wire.description,
  vision: wire.vision,
  mission: wire.mission,
  values: wire.values,
  type: wire.type,
  registrationNumber: wire.registrationNumber,
  registrationDate: wire.registrationDate,
  legalEntity: wire.legalEntity,
  contactInformation: wire.contactInformation,
  owner: {},
  address: wire.address ?? {},
  contact: wire.contact ?? {},
  social_media: wire.social_media ?? {},
  createdBy: wire.createdBy ?? {},
  deletedAt: wire.deletedAt,
  deletedBy: wire.deletedBy ?? null,
  createdAt: wire.createdAt ?? wire.created_at,
  updatedAt: wire.updatedAt ?? wire.updated_at,
});

const isFilled = (v: unknown): boolean =>
  typeof v === "string" ? v.trim() !== "" : v != null;

/** When API sends a typed board label, validate against enum; else fall back. */
export const parseBoardTypeFromWire = (
  raw: unknown,
  fallback: TInstituteType,
): TInstituteType => {
  if (typeof raw === "string" && BOARD_TYPE_SET.has(raw))
    return raw as TInstituteType;
  return fallback;
};

const resolveTrimmedWireString = (
  wireVal: unknown,
  embeddedVal: string | undefined,
): string => {
  if (typeof wireVal === "string" && wireVal.trim() !== "") return wireVal.trim();
  if (typeof embeddedVal === "string" && embeddedVal.trim() !== "") {
    return embeddedVal.trim();
  }
  return "";
};

const resolveOptionalWireDateString = (
  wireVal: unknown,
  embeddedVal: string | undefined,
): string | undefined => {
  if (typeof wireVal === "string" && wireVal.trim() !== "") return wireVal.trim();
  if (typeof embeddedVal === "string" && embeddedVal.trim() !== "") {
    return embeddedVal.trim();
  }
  return undefined;
};

/** First non-empty string among alternate wire keys (camelCase + snake_case). */
const firstNonEmptyStringField = (
  rec: Record<string, unknown>,
  ...keys: string[]
): string | undefined => {
  for (const k of keys) {
    const v = rec[k];
    if (typeof v === "string" && v.trim() !== "") return v.trim();
  }
  return undefined;
};

const mapDeletedByFromWire = (
  raw: unknown,
): TInstitute["deletedBy"] | undefined => {
  if (raw == null || typeof raw !== "object") return undefined;
  return raw as TInstitute["deletedBy"];
};

const instituteWireAuditTimestamps = (
  wire: TGetInstituteWire,
): { createdAt?: string; updatedAt?: string } => {
  const flat = wire as unknown as Record<string, unknown>;
  return {
    createdAt: firstNonEmptyStringField(flat, "createdAt", "created_at"),
    updatedAt: firstNonEmptyStringField(flat, "updatedAt", "updated_at"),
  };
};

const contactEmailPrimarySecondaryFromRecords = (
  contactInformation: Record<string, unknown> | null | undefined,
  contact: Record<string, unknown>,
): {
  emailNorm: ReturnType<typeof normalizeWireContactEmail>;
  primaryPhone?: string;
  secondaryPhone?: string;
  website?: string;
  fax?: string;
} => {
  const ci =
    contactInformation && typeof contactInformation === "object"
      ? contactInformation
      : null;

  const emailFromCi = normalizeWireContactEmail(ci?.email);
  const emailFromContact = normalizeWireContactEmail(contact?.email);
  const emailNorm =
    emailFromCi.email || emailFromCi.isVerified !== undefined
      ? emailFromCi
      : emailFromContact;

  const ciRec = ci ?? undefined;
  const secondaryPhone =
    ciRec != null
      ? firstNonEmptyStringField(ciRec, "secondaryPhone", "secondary_phone")
      : undefined;

  const primaryFromCi =
    ciRec != null
      ? firstNonEmptyStringField(
          ciRec,
          "primaryPhone",
          "primary_phone",
          "phoneNumber",
          "phone_number",
        )
      : undefined;

  const primaryPhone =
    primaryFromCi ??
    firstNonEmptyStringField(contact, "phoneNumber", "phone_number");

  return {
    emailNorm,
    primaryPhone,
    secondaryPhone,
    website:
      ciRec != null
        ? firstNonEmptyStringField(ciRec, "website")
        : undefined,
    fax: ciRec != null ? firstNonEmptyStringField(ciRec, "fax") : undefined,
  };
};

const ADDRESS_MEANINGFUL_KEYS = [
  "street1",
  "street2",
  "streetAddress",
  "street_address",
  "line1",
  "line2",
  "city",
  "state",
  "country",
  "pinCode",
  "postalCode",
] as const;

const isWireAddressMeaningful = (
  addr: Record<string, unknown> | null | undefined,
): boolean => {
  if (!addr || typeof addr !== "object") return false;
  return ADDRESS_MEANINGFUL_KEYS.some((k) => isFilled(addr[k]));
};

const isWireSocialMeaningful = (
  social: Record<string, unknown> | null | undefined,
): boolean => {
  if (!social || typeof social !== "object") return false;
  return Object.values(social).some((v) => isFilled(v));
};

export type TInstituteConfigurationCompletenessInput = {
  status: TInstituteStatus;
  name?: string;
  /** Resolved board / type shown on the institute card. */
  resolvedType: TInstitute["type"];
  /** True when `_adminLegacy` is present (string JSON or structured `values`). */
  hasAdminLegacyValuesBlob: boolean;
  /** Non-empty API `type` string (even unknown board labels count as “specified”). */
  hasExplicitWireBoardTypeRaw: boolean;
  registrationNumber?: string;
  registrationDate?: string;
  legalEntity?: string;
  email?: string;
  primaryPhone?: string;
  vision?: string;
  mission?: string;
  address?: Record<string, unknown> | null;
  socialMedia?: Record<string, unknown> | null;
};

/**
 * Profile readiness for list/detail cards. Uses more wire fields than the legacy 7-slot
 * check so institutes with richer API data score higher (the old 3/7 ≈ 43% collapse).
 * Includes lifecycle `active` as one criterion so pending/draft caps below "fully live".
 */
export const computeInstituteConfigurationCompleteness = (
  input: TInstituteConfigurationCompletenessInput,
): number => {
  const typeSpecified =
    input.hasAdminLegacyValuesBlob ||
    input.resolvedType !== "Other" ||
    input.hasExplicitWireBoardTypeRaw;

  const checks: boolean[] = [
    isFilled(input.name),
    typeSpecified,
    isFilled(input.registrationNumber),
    isFilled(input.registrationDate),
    isFilled(input.legalEntity),
    isFilled(input.email),
    isFilled(input.primaryPhone),
    isFilled(input.vision),
    isFilled(input.mission),
    isWireAddressMeaningful(input.address ?? undefined),
    isWireSocialMeaningful(input.socialMedia ?? undefined),
    input.status === "active",
  ];

  const present = checks.filter(Boolean).length;
  return Math.round((present / checks.length) * 100);
};

/** Best-effort map from institute list/detail wire DTO → UI institute model */
export const mapGetInstituteWireToTInstitute = (
  wire: TGetInstituteWire,
): TInstitute => {
  const embedded = parseLegacyEmbeddedFromWireValues(wire.values);
  const contact = (wire.contact ?? {}) as Record<string, unknown>;
  const { emailNorm, primaryPhone, secondaryPhone, website, fax } =
    contactEmailPrimarySecondaryFromRecords(
      wire.contactInformation,
      contact,
    );

  const type = parseBoardTypeFromWire(wire.type, embedded?.type ?? "Other");
  const registrationNumber = resolveTrimmedWireString(
    wire.registrationNumber,
    embedded?.registrationNumber,
  );
  const registrationDate = resolveOptionalWireDateString(
    wire.registrationDate,
    embedded?.registrationDate,
  );
  const legalEntity =
    resolveTrimmedWireString(wire.legalEntity, embedded?.legalEntity) ||
    wire.description;
  const status = parseWireInstituteStatus(wire.status);

  const hasContact =
    !!emailNorm.email ||
    !!primaryPhone ||
    !!secondaryPhone ||
    !!website ||
    !!fax;

  const { createdAt, updatedAt } = instituteWireAuditTimestamps(wire);

  return {
    id: wire.id,
    name: wire.name,
    type,
    registrationNumber,
    registrationDate,
    legalEntity,
    status,
    configurationCompleteness: computeInstituteConfigurationCompleteness({
      status,
      name: wire.name,
      resolvedType: type,
      hasAdminLegacyValuesBlob: embedded != null,
      hasExplicitWireBoardTypeRaw:
        typeof wire.type === "string" && wire.type.trim() !== "",
      registrationNumber,
      registrationDate,
      legalEntity,
      email: emailNorm.email,
      primaryPhone,
      vision: wire.vision,
      mission: wire.mission,
      address: wire.address,
      socialMedia: wire.social_media,
    }),
    contactInformation: hasContact
      ? {
          primaryPhone,
          secondaryPhone,
          email: emailNorm.email,
          emailVerified: emailNorm.isVerified,
          website,
          fax,
        }
      : undefined,
    addresses: undefined,
    createdAt,
    updatedAt,
    deletedAt: wire.deletedAt,
    deletedBy: mapDeletedByFromWire(wire.deletedBy),
  };
};

export const mapCreateWireResponseToTInstitute = (
  response: TCreateInstituteWireResponse,
  request: TCreateInstituteWireRequest,
  desiredCreateStatus?: TInstituteCreateStatus,
): TInstitute => {
  const parsed = parseLegacyEmbeddedFromWireValues(request.values);
  const embedded =
    parsed ??
    ({
      type: "Other" as const,
      registrationNumber: "",
      registrationDate: "",
      legalEntity: request.description,
    } satisfies TLegacyEmbedded);

  const name = response.name ?? request.name;
  const status = parseWireInstituteStatus(
    response.status ?? desiredCreateStatus,
    "pending",
  );
  const createContact = response.contact as Record<string, unknown>;
  const { emailNorm, primaryPhone, secondaryPhone, website, fax } =
    contactEmailPrimarySecondaryFromRecords(
      response.contactInformation,
      createContact,
    );

  const type = parseBoardTypeFromWire(response.type, embedded.type);
  const registrationNumber = resolveTrimmedWireString(
    response.registrationNumber,
    embedded.registrationNumber,
  );
  const registrationDate = resolveOptionalWireDateString(
    response.registrationDate,
    embedded.registrationDate,
  );
  const legalEntity =
    resolveTrimmedWireString(response.legalEntity, embedded.legalEntity) ||
    response.description;

  const hasContact =
    !!emailNorm.email ||
    !!primaryPhone ||
    !!secondaryPhone ||
    !!website ||
    !!fax;

  return {
    id: response.id ?? NIL_UUID,
    name,
    type,
    registrationNumber,
    registrationDate,
    legalEntity,
    status,
    configurationCompleteness: computeInstituteConfigurationCompleteness({
      status,
      name,
      resolvedType: type,
      hasAdminLegacyValuesBlob: !!parsed,
      hasExplicitWireBoardTypeRaw:
        typeof response.type === "string" && response.type.trim() !== "",
      registrationNumber,
      registrationDate,
      legalEntity,
      email: emailNorm.email,
      primaryPhone,
      vision: response.vision,
      mission: response.mission,
      address: response.address,
      socialMedia: undefined,
    }),
    contactInformation: hasContact
      ? {
          primaryPhone,
          secondaryPhone,
          email: emailNorm.email,
          emailVerified: emailNorm.isVerified,
          website,
          fax,
        }
      : undefined,
    addresses: [],
  };
};

/** POST/PATCH payloads mirroring GET by-id include `ownerId` + `values`; domain `TInstitute` omits both. */
export const looksLikeWireInstituteByIdResponse = (
  body: unknown,
): body is TGetInstituteByIdWire =>
  typeof body === "object" &&
  body !== null &&
  typeof (body as { id?: unknown }).id === "string" &&
  typeof (body as { ownerId?: unknown }).ownerId === "string" &&
  Object.prototype.hasOwnProperty.call(body, "values");

export const coerceInstituteMutationWireResponseBody = (
  body: unknown,
): TInstitute | null =>
  looksLikeWireInstituteByIdResponse(body)
    ? mapGetInstituteWireToTInstitute(normalizeGetInstituteByIdWire(body))
    : null;
